import { z } from 'zod';
import { isoTimestampSchema, uuidSchema } from './common';

const date = z.iso.date();
const positive = z.number().int().positive().refine(Number.isSafeInteger);
const nonNegative = z.number().int().nonnegative().refine(Number.isSafeInteger);
export const taxTreatmentSchema = z.enum(['exclusive', 'inclusive', 'exempt']);
export const commercialSourceSchema = z.enum(['quote', 'agreement']);

export const supplierItemSchema = z.object({
  id: uuidSchema,
  supplier_id: uuidSchema,
  supplier_name: z.string(),
  item_id: uuidSchema,
  sku: z.string(),
  item_name: z.string(),
  base_unit: z.enum(['g', 'ml', 'pcs']),
  supplier_sku: z.string().nullable(),
  purchase_unit: z.string(),
  unit_factor_scaled: positive,
  lead_time_days: nonNegative,
  min_order_qty_scaled: nonNegative,
  order_multiple_scaled: positive,
  tax_treatment: taxTreatmentSchema,
  tax_bps: z.number().int().min(0).max(10_000),
  active: z.boolean(),
  version: z.number().int(),
});
export type SupplierItem = z.infer<typeof supplierItemSchema>;
export const supplierItemListSchema = z.object({ items: z.array(supplierItemSchema) });

export const upsertSupplierItemRequestSchema = z.object({
  supplier_id: uuidSchema,
  item_id: uuidSchema,
  supplier_sku: z.string().trim().min(1).max(60).nullable().default(null),
  purchase_unit: z.string().trim().min(1).max(12),
  lead_time_days: z.number().int().min(0).max(365).default(0),
  min_order_qty_scaled: nonNegative.default(0),
  order_multiple_scaled: positive.default(1_000),
  tax_treatment: taxTreatmentSchema.default('exclusive'),
  tax_bps: z.number().int().min(0).max(10_000).default(0),
  active: z.boolean().default(true),
});

export const commercialTermSchema = z.object({
  id: uuidSchema,
  supplier_item_id: uuidSchema,
  supplier_id: uuidSchema,
  supplier_name: z.string(),
  item_id: uuidSchema,
  sku: z.string(),
  item_name: z.string(),
  source_type: commercialSourceSchema,
  reference: z.string(),
  version_no: z.number().int(),
  status: z.enum(['current', 'superseded', 'cancelled']),
  rfq_id: uuidSchema.nullable(),
  rfq_line_id: uuidSchema.nullable(),
  unit: z.string(),
  unit_factor_scaled: positive,
  unit_price_minor: nonNegative,
  tax_treatment: taxTreatmentSchema,
  tax_bps: z.number().int(),
  net_unit_price_minor: nonNegative,
  normalized_unit: z.string(),
  normalized_net_price_minor: nonNegative,
  valid_from: date,
  valid_to: date,
  valid_now: z.boolean(),
  min_order_qty_scaled: nonNegative,
  order_multiple_scaled: positive,
  lead_time_days: nonNegative,
  created_at: isoTimestampSchema,
});
export type CommercialTerm = z.infer<typeof commercialTermSchema>;
export const commercialTermListSchema = z.object({ items: z.array(commercialTermSchema) });

const termFields = {
  supplier_item_id: uuidSchema,
  reference: z.string().trim().min(1).max(80),
  unit: z.string().trim().min(1).max(12),
  unit_price_minor: nonNegative,
  tax_treatment: taxTreatmentSchema,
  tax_bps: z.number().int().min(0).max(10_000),
  valid_from: date,
  valid_to: date,
  min_order_qty_scaled: nonNegative,
  order_multiple_scaled: positive,
  lead_time_days: z.number().int().min(0).max(365),
};
export const createAgreementRequestSchema = z
  .object(termFields)
  .refine((v) => v.valid_from <= v.valid_to, {
    message: 'End date must be on or after start date.',
    path: ['valid_to'],
  });

export const rfqLineInputSchema = z.object({
  item_id: uuidSchema,
  unit: z.string().trim().min(1).max(12),
  qty_scaled: positive,
});
export const createRfqRequestSchema = z.object({
  title: z.string().trim().min(3).max(120),
  purpose: z.string().trim().min(1).max(300).nullable().default(null),
  response_due: date.nullable().default(null),
  lines: z.array(rfqLineInputSchema).min(1).max(100),
});
export const rfqActionRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
});
export const createRfqQuoteRequestSchema = z
  .object({
    expected_version: z.number().int().nonnegative(),
    supplier_id: uuidSchema,
    reference: z.string().trim().min(1).max(80),
    valid_from: date,
    valid_to: date,
    lines: z
      .array(
        z.object({
          rfq_line_id: uuidSchema,
          unit: z.string().trim().min(1).max(12),
          unit_price_minor: nonNegative,
          tax_treatment: taxTreatmentSchema,
          tax_bps: z.number().int().min(0).max(10_000),
          min_order_qty_scaled: nonNegative.default(0),
          order_multiple_scaled: positive.default(1_000),
          lead_time_days: z.number().int().min(0).max(365).default(0),
        }),
      )
      .min(1)
      .max(100),
  })
  .refine((v) => v.valid_from <= v.valid_to, {
    message: 'End date must be on or after start date.',
    path: ['valid_to'],
  });
export const awardRfqRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  supplier_id: uuidSchema,
  reference: z.string().trim().min(1).max(80),
  reason: z.string().trim().min(3).max(500),
});

export const rfqLineSchema = z.object({
  id: uuidSchema,
  line_no: z.number().int(),
  item_id: uuidSchema,
  sku: z.string(),
  item_name: z.string(),
  base_unit: z.enum(['g', 'ml', 'pcs']),
  unit: z.string(),
  unit_factor_scaled: positive,
  qty_scaled: positive,
});
export const rfqComparisonSchema = z.object({
  supplier_id: uuidSchema,
  supplier_name: z.string(),
  reference: z.string(),
  valid_from: date,
  valid_to: date,
  valid_now: z.boolean(),
  complete: z.boolean(),
  total_normalized_minor: nonNegative,
  lines: z.array(commercialTermSchema),
});
export const rfqSchema = z.object({
  id: uuidSchema,
  rfq_number: z.string(),
  title: z.string(),
  purpose: z.string().nullable(),
  status: z.enum(['draft', 'issued', 'awarded', 'closed', 'cancelled']),
  response_due: date.nullable(),
  awarded_supplier_id: uuidSchema.nullable(),
  awarded_supplier_name: z.string().nullable(),
  awarded_reference: z.string().nullable(),
  awarded_at: isoTimestampSchema.nullable(),
  created_at: isoTimestampSchema,
  version: z.number().int(),
  lines: z.array(rfqLineSchema),
  comparisons: z.array(rfqComparisonSchema),
});
export type RequestForQuotation = z.infer<typeof rfqSchema>;
export const rfqListSchema = z.object({
  items: z.array(rfqSchema.omit({ lines: true, comparisons: true })),
});
